/**
 * Estrous synchronization protocol presets (farm reference templates).
 * Doses are typical field references — always confirm with your vet and product label.
 */

export const SYNC_SPECIES_LABELS = {
  cattle: 'Cattle',
  buffalo: 'Buffalo',
  sheep: 'Sheep',
  goat: 'Goat',
  camel: 'Camel',
  horse: 'Horse',
  donkey: 'Donkey',
};

/** @typedef {'inject'|'insert'|'remove'|'observe'|'insemination'} SyncActionType */

/**
 * @typedef {Object} SyncProtocolStepTemplate
 * @property {string} key
 * @property {number} dayOffset Day relative to Day 0
 * @property {string} title
 * @property {SyncActionType} action
 * @property {string|null} hormone
 * @property {string|null} productExamples
 * @property {string|null} dosage
 * @property {string|null} route
 * @property {string} defaultTime HH:mm
 * @property {string|null} instructions
 */

/**
 * @typedef {Object} SyncProtocolPreset
 * @property {string} id
 * @property {string} name
 * @property {string[]} species
 * @property {number} stepCount
 * @property {string} summary
 * @property {string} sourceNote
 * @property {SyncProtocolStepTemplate[]} steps
 */

/** @type {SyncProtocolPreset[]} */
export const SYNC_PROTOCOL_PRESETS = [
  {
    id: 'cattle-ovsynch',
    name: 'Ovsynch (Timed AI)',
    species: ['cattle', 'buffalo'],
    stepCount: 4,
    summary: 'GnRH → PG → GnRH → timed AI (~10 days). Standard dairy TAI protocol.',
    sourceNote: 'Widely used 3-hormone timed AI protocol for cattle and buffalo.',
    steps: [
      {
        key: 'g0-gnrh',
        dayOffset: 0,
        title: '1st GnRH injection',
        action: 'inject',
        hormone: 'GnRH',
        productExamples: 'Factrel, Ovalec, Receptal',
        dosage: '2 ml',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Day 0 — start of protocol. Give the first GnRH injection in the neck (IM). Animals can be at any stage of the oestrous cycle. Record batch number and injection site.',
      },
      {
        key: 'g7-pg',
        dayOffset: 7,
        title: 'Prostaglandin (PGF2α)',
        action: 'inject',
        hormone: 'PGF2α',
        productExamples: 'Lutalyse, Estrumate, Enzaprost',
        dosage: '2 ml',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Exactly 7 days after the first GnRH. Prostaglandin causes regression of the corpus luteum if present. Watch for oestrus signs from 24–72 h after this injection.',
      },
      {
        key: 'g9-gnrh',
        dayOffset: 9,
        title: '2nd GnRH injection',
        action: 'inject',
        hormone: 'GnRH',
        productExamples: 'Factrel, Ovalec, Receptal',
        dosage: '2 ml',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Give the second GnRH 48 hours after prostaglandin (standard Ovsynch timing). This ovulates the follicle for timed breeding.',
      },
      {
        key: 'g10-ai',
        dayOffset: 10,
        title: 'Timed artificial insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Perform timed AI 16–20 hours after the second GnRH. Thaw semen correctly, load the AI gun, and record straw ID, bull code, technician, and any heat signs even though timing is fixed.',
      },
    ],
  },
  {
    id: 'cattle-7day-cidr-cosynch',
    name: '7-Day Co-Synch + CIDR',
    species: ['cattle', 'buffalo'],
    stepCount: 4,
    summary: 'CIDR insert + GnRH → remove CIDR + PG → GnRH → timed AI.',
    sourceNote: 'Common CIDR-based protocol for anoestrus or herd synchronization.',
    steps: [
      {
        key: 'd0-cidr-gnrh',
        dayOffset: 0,
        title: 'Insert CIDR + GnRH',
        action: 'insert',
        hormone: 'Progesterone (CIDR)',
        productExamples: 'CIDR 1900 (cattle), PRID (where used)',
        dosage: '1 device + GnRH 2 ml IM',
        route: 'Intravaginal + IM',
        defaultTime: '08:00',
        instructions: 'Insert progesterone device and give GnRH same day.',
      },
      {
        key: 'd7-remove-pg',
        dayOffset: 7,
        title: 'Remove CIDR + prostaglandin',
        action: 'remove',
        hormone: 'PGF2α',
        productExamples: 'Lutalyse, Estrumate',
        dosage: 'Remove device + 2 ml IM',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Remove CIDR and inject PG immediately.',
      },
      {
        key: 'd9-gnrh',
        dayOffset: 9,
        title: '2nd GnRH injection',
        action: 'inject',
        hormone: 'GnRH',
        productExamples: 'Factrel, Ovalec',
        dosage: '2 ml',
        route: 'IM',
        defaultTime: '08:00',
        instructions: '48 hours after CIDR removal + PG.',
      },
      {
        key: 'd10-ai',
        dayOffset: 10,
        title: 'Timed artificial insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Timed AI 16–20 h after 2nd GnRH.',
      },
    ],
  },
  {
    id: 'cattle-5day-cidr-cosynch',
    name: '5-Day Co-Synch + CIDR',
    species: ['cattle'],
    stepCount: 4,
    summary: 'Short CIDR program — 5-day device with Co-Synch timing.',
    sourceNote: 'Shorter progesterone exposure; common in dairy herds.',
    steps: [
      {
        key: 'd0-cidr-gnrh',
        dayOffset: 0,
        title: 'Insert CIDR + GnRH',
        action: 'insert',
        hormone: 'Progesterone (CIDR)',
        productExamples: 'CIDR 1900',
        dosage: '1 device + GnRH 2 ml IM',
        route: 'Intravaginal + IM',
        defaultTime: '08:00',
        instructions: 'Day 0 — insert CIDR and give GnRH.',
      },
      {
        key: 'd5-remove-pg',
        dayOffset: 5,
        title: 'Remove CIDR + prostaglandin',
        action: 'remove',
        hormone: 'PGF2α',
        productExamples: 'Lutalyse, Estrumate',
        dosage: 'Remove device + 2 ml IM',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Day 5 — remove CIDR and inject PG.',
      },
      {
        key: 'd7-gnrh',
        dayOffset: 7,
        title: '2nd GnRH injection',
        action: 'inject',
        hormone: 'GnRH',
        productExamples: 'Factrel, Ovalec',
        dosage: '2 ml',
        route: 'IM',
        defaultTime: '08:00',
        instructions: '48 hours after CIDR removal.',
      },
      {
        key: 'd8-ai',
        dayOffset: 8,
        title: 'Timed artificial insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Timed AI 16–20 h after 2nd GnRH.',
      },
    ],
  },
  {
    id: 'cattle-pg-heat-ai',
    name: 'PG Single-Shot + Heat AI',
    species: ['cattle', 'buffalo'],
    stepCount: 2,
    summary: 'One PG injection then AI on observed standing heat.',
    sourceNote: 'Simple resynchronization when cycling animals are present.',
    steps: [
      {
        key: 'd0-pg',
        dayOffset: 0,
        title: 'Prostaglandin injection',
        action: 'inject',
        hormone: 'PGF2α',
        productExamples: 'Lutalyse, Estrumate',
        dosage: '2 ml',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Animal should have a functional CL for best response.',
      },
      {
        key: 'heat-ai',
        dayOffset: 2,
        title: 'AI on detected heat',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Observe AM/PM. AI 12–18 h after standing heat is first seen.',
      },
    ],
  },
  {
    id: 'sheep-14day-sponge',
    name: '14-Day FGA Sponge + PG + eCG',
    species: ['sheep'],
    stepCount: 3,
    summary: 'Fluorogestone sponge 14 days → remove + PG + eCG → AI 48–58 h later.',
    sourceNote: 'Standard out-of-season / flock synchronization approach for sheep.',
    steps: [
      {
        key: 'd0-sponge',
        dayOffset: 0,
        title: 'Insert FGA sponge',
        action: 'insert',
        hormone: 'Progesterone (FGA sponge)',
        productExamples: 'Chronogest, FGA-impregnated sponge',
        dosage: '1 sponge (30–40 mg FGA)',
        route: 'Intravaginal',
        defaultTime: '08:00',
        instructions: 'Day 0 — insert sponge with antibiotic lube if farm SOP requires.',
      },
      {
        key: 'd14-remove-pg-ecg',
        dayOffset: 14,
        title: 'Remove sponge + PG + eCG',
        action: 'remove',
        hormone: 'PGF2α + eCG',
        productExamples: 'Cloprostenol + PMSG/eCG',
        dosage: 'PG 2 ml IM + eCG 400–500 IU IM (typical ewe)',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Remove sponge and inject PG and eCG at removal.',
      },
      {
        key: 'd16-ai',
        dayOffset: 16,
        title: 'Artificial insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '10:00',
        instructions: 'AI ~48–58 h after sponge removal (laparoscopic or cervical per farm setup).',
      },
    ],
  },
  {
    id: 'goat-11day-sponge',
    name: '11-Day FGA Sponge + PG + eCG',
    species: ['goat'],
    stepCount: 3,
    summary: 'Shorter sponge program commonly used in goats.',
    sourceNote: 'Typical goat synchronization using intravaginal progesterone.',
    steps: [
      {
        key: 'd0-sponge',
        dayOffset: 0,
        title: 'Insert FGA sponge',
        action: 'insert',
        hormone: 'Progesterone (FGA sponge)',
        productExamples: 'Fluorogestone sponge (goat)',
        dosage: '1 sponge',
        route: 'Intravaginal',
        defaultTime: '08:00',
        instructions: 'Day 0 — insert sponge.',
      },
      {
        key: 'd11-remove-pg-ecg',
        dayOffset: 11,
        title: 'Remove sponge + PG + eCG',
        action: 'remove',
        hormone: 'PGF2α + eCG',
        productExamples: 'Cloprostenol + eCG (PMSG)',
        dosage: 'PG 1.25 ml IM + eCG 300 IU IM (typical doe)',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Remove sponge; give PG and eCG at removal.',
      },
      {
        key: 'd13-ai',
        dayOffset: 13,
        title: 'Artificial insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '10:00',
        instructions: 'AI ~48 h after sponge removal when using eCG.',
      },
    ],
  },
  {
    id: 'camel-pg-monitor',
    name: 'Camel PG + Monitoring',
    species: ['camel'],
    stepCount: 2,
    summary: 'PG-based follicular wave manipulation with vet monitoring.',
    sourceNote: 'Camel breeding is highly seasonal; confirm protocol with reproduction specialist.',
    steps: [
      {
        key: 'd0-pg',
        dayOffset: 0,
        title: 'Prostaglandin (vet protocol)',
        action: 'inject',
        hormone: 'PGF2α / cloprostenol',
        productExamples: 'Cloprostenol (dose per vet)',
        dosage: 'Per veterinary prescription',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Season and follicular status must be assessed first.',
      },
      {
        key: 'monitor-ai',
        dayOffset: 7,
        title: 'Monitor + insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Ultrasound/behavior monitoring; AI when dominant follicle/ovulation window confirmed.',
      },
    ],
  },
  {
    id: 'horse-pg-short',
    name: 'Mare PG Short-Cycle',
    species: ['horse'],
    stepCount: 2,
    summary: 'PG for mares with active CL; then monitor and AI.',
    sourceNote: 'Horses are usually managed by follicle tracking rather than fixed TAI.',
    steps: [
      {
        key: 'd0-pg',
        dayOffset: 0,
        title: 'Prostaglandin (if CL present)',
        action: 'inject',
        hormone: 'PGF2α',
        productExamples: 'Lutalyse, Estrumate',
        dosage: '1 ml IM (mare — confirm label)',
        route: 'IM',
        defaultTime: '08:00',
        instructions: 'Only effective with functional corpus luteum.',
      },
      {
        key: 'follicle-ai',
        dayOffset: 3,
        title: 'Follicle check + insemination',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Transrectal ultrasound; AI near ovulation (often ~48–96 h post-PG).',
      },
    ],
  },
  {
    id: 'donkey-heat-natural',
    name: 'Donkey — Heat Observation + AI/Cover',
    species: ['donkey'],
    stepCount: 1,
    summary: 'Natural estrus detection; insemination or cover when in heat.',
    sourceNote: 'Donkeys are rarely on multi-step hormone programs in field conditions.',
    steps: [
      {
        key: 'heat-service',
        dayOffset: 0,
        title: 'Service on observed heat',
        action: 'insemination',
        hormone: null,
        productExamples: null,
        dosage: null,
        route: null,
        defaultTime: '08:00',
        instructions: 'Observe for heat signs; breed every 48 h while receptive if natural cover.',
      },
    ],
  },
];

export function getSyncPresetsForSpecies(speciesKey) {
  if (!speciesKey) return SYNC_PROTOCOL_PRESETS;
  const key = speciesKey.toLowerCase();
  return SYNC_PROTOCOL_PRESETS.filter((p) => p.species.includes(key));
}

export function getSyncPresetById(presetId) {
  return SYNC_PROTOCOL_PRESETS.find((p) => p.id === presetId) || null;
}
